/**
 * Excel tables (`data/global/excel/*.txt`): tab-separated with a header row. LoD tables contain
 * "Expansion" divider rows (first column "Expansion", rest blank), which are dropped.
 */
export class ExcelTable {
  readonly columns: string[];
  readonly rows: string[][];
  /**
   * Index of the first row after a dropped "Expansion" divider (-1 when the table has none). The
   * engine resolves some numeric references per section, e.g. Levels.txt monster lists.
   */
  readonly expansionStart: number = -1;
  private readonly colIndex: Map<string, number>;

  constructor(text: string) {
    const lines = text.split(/\r?\n/);
    this.columns = (lines[0] ?? '').split('\t');
    this.colIndex = new Map();
    // First occurrence wins for duplicate headers (a few tables repeat a column name).
    this.columns.forEach((c, i) => {
      const k = c.toLowerCase();
      if (!this.colIndex.has(k)) this.colIndex.set(k, i);
    });
    this.rows = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line) continue;
      const cells = line.split('\t');
      if (cells[0] === 'Expansion' && cells.slice(1).every((c) => c === '')) {
        if (this.expansionStart < 0) this.expansionStart = this.rows.length;
        continue;
      }
      if (cells.every((c) => c === '')) continue;
      this.rows.push(cells);
    }
  }

  has(col: string): boolean {
    return this.colIndex.has(col.toLowerCase());
  }

  col(name: string): number {
    const i = this.colIndex.get(name.toLowerCase());
    if (i === undefined) throw new Error(`excel: no column "${name}"`);
    return i;
  }

  /** Row accessor bound to column names. */
  row(i: number): ExcelRow {
    return new ExcelRow(this, this.rows[i]);
  }

  *[Symbol.iterator](): IterableIterator<ExcelRow> {
    for (const r of this.rows) yield new ExcelRow(this, r);
  }

  get length(): number {
    return this.rows.length;
  }
}

export class ExcelRow {
  constructor(
    private readonly table: ExcelTable,
    readonly cells: string[],
  ) {}
  /** Whether the table has this column. */
  has(col: string): boolean {
    return this.table.has(col);
  }
  str(col: string): string {
    const i = this.table.has(col) ? this.table.col(col) : -1;
    return i >= 0 ? (this.cells[i] ?? '').trim() : '';
  }
  /** Integer value; blank → `fallback`. */
  int(col: string, fallback = 0): number {
    const s = this.str(col);
    if (s === '') return fallback;
    const v = parseInt(s, 10);
    return Number.isNaN(v) ? fallback : v;
  }
  num(col: string, fallback = 0): number {
    const s = this.str(col);
    if (s === '') return fallback;
    const v = Number(s);
    return Number.isNaN(v) ? fallback : v;
  }
  bool(col: string): boolean {
    const s = this.str(col);
    return s !== '' && s !== '0';
  }
}
